import { Percent } from '@pancakeswap/sdk'
import type { Address, PublicClient } from 'viem'
import { priceImpact, spotOutV2, spotOutV3 } from 'multiswap/impact'
import { erc20Abi, factoryAbi, poolAbi, quoterV2Abi, v2RouterAbi } from './abi'
import { PoolDex, dexOfFactory, lc } from './dexes'
import type { PoolFacts, ResolvePool } from './route'

/**
 * Everything the trade panel reads from the chain about ONE pool, and the quote for one trade. All reads go to the exchange's own contracts
 * (its pool, its factory, its quoter or router), never to an aggregator, so a number on screen is what that pool will do.
 */

export interface TokenInfo {
  address: Address
  symbol: string
  decimals: number
}

export type PoolState = { kind: 'v3'; sqrtPriceX96: bigint; liquidity: bigint } | { kind: 'v2'; reserve0: bigint; reserve1: bigint }

export interface PoolView {
  facts: PoolFacts
  dex: PoolDex
  token0: TokenInfo
  token1: TokenInfo
  state: PoolState
}

export type PoolErrorCode = 'not_a_pool' | 'unsupported_exchange' | 'unreadable'
export class PoolError extends Error {
  readonly code: PoolErrorCode

  constructor(code: PoolErrorCode, message: string) {
    super(message)
    this.name = 'PoolError'
    this.code = code
  }
}

const ok = <T>(r: { status: string; result?: unknown } | undefined): T | undefined => (r && r.status === 'success' ? (r.result as T) : undefined)

/** The pool's identity (factory, tokens, fee), which exchange it belongs to, both tokens' symbols, and the state the price impact is computed from. */
export async function readPoolView(client: PublicClient, pool: Address): Promise<PoolView> {
  const head = await client.multicall({
    allowFailure: true,
    contracts: [
      { address: pool, abi: poolAbi, functionName: 'factory' },
      { address: pool, abi: poolAbi, functionName: 'token0' },
      { address: pool, abi: poolAbi, functionName: 'token1' },
    ] as const,
  })
  const factory = ok<Address>(head[0])
  const t0 = ok<Address>(head[1])
  const t1 = ok<Address>(head[2])
  if (!factory || !t0 || !t1) throw new PoolError('not_a_pool', 'This address is not a pool')
  const dex = dexOfFactory(factory)
  if (!dex) throw new PoolError('unsupported_exchange', 'This pool belongs to an exchange this page cannot trade on')

  // the calls mix three ABIs, which the library's tuple types cannot follow: the answers are read back through `ok<T>` below
  const extra = await client.multicall({
    allowFailure: true,
    contracts: [
      { address: t0, abi: erc20Abi, functionName: 'symbol' },
      { address: t0, abi: erc20Abi, functionName: 'decimals' },
      { address: t1, abi: erc20Abi, functionName: 'symbol' },
      { address: t1, abi: erc20Abi, functionName: 'decimals' },
      ...(dex.kind === 'v3'
        ? [
            { address: pool, abi: poolAbi, functionName: 'fee' } as const,
            { address: pool, abi: poolAbi, functionName: 'slot0' } as const,
            { address: pool, abi: poolAbi, functionName: 'liquidity' } as const,
          ]
        : [{ address: pool, abi: poolAbi, functionName: 'getReserves' } as const]),
    ] as any,
  }) as Array<{ status: string; result?: unknown }>
  const s0 = ok<string>(extra[0])
  const d0 = ok<number>(extra[1])
  const s1 = ok<string>(extra[2])
  const d1 = ok<number>(extra[3])
  if (d0 === undefined || d1 === undefined) throw new PoolError('unreadable', 'The pool\'s tokens could not be read')
  let fee: number | null = null
  let state: PoolState
  if (dex.kind === 'v3') {
    const f = ok<number>(extra[4])
    const slot0 = ok<readonly [bigint, number]>(extra[5])
    const liquidity = ok<bigint>(extra[6])
    if (f === undefined || !slot0 || liquidity === undefined) throw new PoolError('unreadable', 'The pool\'s price could not be read')
    fee = Number(f)
    state = { kind: 'v3', sqrtPriceX96: slot0[0], liquidity }
  } else {
    const r = ok<readonly [bigint, bigint, number]>(extra[4])
    if (!r) throw new PoolError('unreadable', 'The pool\'s reserves could not be read')
    state = { kind: 'v2', reserve0: r[0], reserve1: r[1] }
  }
  return {
    facts: { pool, factory, token0: t0, token1: t1, fee },
    dex,
    token0: { address: t0, symbol: s0 ?? 'TOKEN0', decimals: Number(d0) },
    token1: { address: t1, symbol: s1 ?? 'TOKEN1', decimals: Number(d1) },
    state,
  }
}

/** The exchange's own answer to "which pool do you trade these tokens in": the last word of the route check. */
export const makeResolver =
  (client: PublicClient): ResolvePool =>
  async (kind, factory, a, b, fee) => {
    if (kind === 'v3') {
      return (await client.readContract({ address: factory, abi: factoryAbi, functionName: 'getPool', args: [a, b, fee ?? 0] })) as Address
    }
    return (await client.readContract({ address: factory, abi: factoryAbi, functionName: 'getPair', args: [a, b] })) as Address
  }

/** Output for an exact input through this one pool: the exchange's QuoterV2 (V3) or Router02.getAmountsOut with the direct path (V2). */
export async function quoteTrade(client: PublicClient, view: PoolView, tokenIn: Address, tokenOut: Address, amountIn: bigint): Promise<bigint> {
  const { dex, facts } = view
  if (dex.kind === 'v3') {
    if (!dex.quoter || facts.fee === null) throw new PoolError('unreadable', 'No quoter for this exchange')
    const { result } = await client.simulateContract({
      address: dex.quoter,
      abi: quoterV2Abi,
      functionName: 'quoteExactInputSingle',
      args: [{ tokenIn, tokenOut, amountIn, fee: facts.fee, sqrtPriceLimitX96: 0n }],
    })
    return result[0]
  }
  const amounts = (await client.readContract({ address: dex.router, abi: v2RouterAbi, functionName: 'getAmountsOut', args: [amountIn, [tokenIn, tokenOut]] })) as readonly bigint[]
  return amounts[amounts.length - 1]
}

export interface Impact {
  /** vs the pool's price before the trade, the LP fee taken out (the figure the standard Swap page warns on). */
  withoutFee: Percent
  /** LP fee included. */
  raw: Percent
  lpFee: Percent
}

/** Price impact of one trade: the same definition as MultiSwap / the standard Swap (multiswap/impact.ts), fed by this pool's own state. */
export function impactOf(view: PoolView, tokenIn: Address, amountIn: bigint, amountOut: bigint): Impact | null {
  const zeroForOne = lc(tokenIn) === lc(view.facts.token0)
  const { state, dex, facts } = view
  let spot: bigint
  let lpFee: Percent
  if (state.kind === 'v3') {
    spot = spotOutV3([{ sqrtPriceX96: state.sqrtPriceX96, zeroForOne }], amountIn)
    lpFee = new Percent(BigInt(facts.fee ?? 0), 1_000_000n)
  } else {
    spot = spotOutV2([{ reserveIn: zeroForOne ? state.reserve0 : state.reserve1, reserveOut: zeroForOne ? state.reserve1 : state.reserve0 }], amountIn)
    lpFee = new Percent(BigInt(dex.v2FeeBps ?? 25), 10_000n)
  }
  const r = priceImpact(spot, amountOut, lpFee)
  return r ? { withoutFee: r.withoutFee, raw: r.raw, lpFee } : null
}

export interface Account {
  /** Balance of the token being sold (the ERC-20, or WBNB when the leg is native: then `bnb` is the spendable amount). */
  balance: bigint
  allowance: bigint
  bnb: bigint
}

export async function readAccount(client: PublicClient, user: Address, tokenIn: Address, spender: Address): Promise<Account> {
  const [balance, allowance, bnb] = await Promise.all([
    client.readContract({ address: tokenIn, abi: erc20Abi, functionName: 'balanceOf', args: [user] }) as Promise<bigint>,
    client.readContract({ address: tokenIn, abi: erc20Abi, functionName: 'allowance', args: [user, spender] }) as Promise<bigint>,
    client.getBalance({ address: user }),
  ])
  return { balance, allowance, bnb }
}

